import { useState } from 'react'
import HeaderSection from '../components/Shared/HeaderSection'
import SolutionDetail from '../components/Solution/SolutionDetail'
import useSolutionSearch from '../hooks/useSolutionSearch'
import useTicketCatalogs from '../hooks/useTicketCatalogs'
import { htmlToPlainText } from '../utils/richText'

/** Fecha corta para la lista. */
const shortDate = (value) =>
  new Date(value).toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  })

/**
 * Base de soluciones: lista buscable a la izquierda y el procedimiento de la
 * solución seleccionada a la derecha. La página ocupa el alto disponible y
 * cada panel scrollea por dentro; en móvil se ve un panel a la vez.
 */
const Solutions = () => {
  const search = useSolutionSearch()
  const { employeeByAuthId } = useTicketCatalogs()
  const [selectedId, setSelectedId] = useState(null)
  const [showDetailMobile, setShowDetailMobile] = useState(false)

  // Selección explícita o, por defecto, la primera de la lista.
  const selected =
    search.items.find((s) => s.id === selectedId) || search.items[0] || null

  const select = (id) => {
    setSelectedId(id)
    setShowDetailMobile(true)
  }

  return (
    <div className="page-fill solutions-page">
      <HeaderSection
        compact
        title="Base de soluciones"
        subtitle="Procedimientos aplicados para resolver tickets"
        icon="fa-solid fa-lightbulb"
      />

      <div className={`sl-layout ${showDetailMobile ? 'show-detail' : ''}`}>
        {/* LISTA */}
        <aside className="sl-list-pane">
          <div className="sl-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="search"
              placeholder="Buscar por título o contenido…"
              value={search.query}
              onChange={(e) => search.setQuery(e.target.value)}
              aria-label="Buscar soluciones"
            />
          </div>
          <div className="sl-count">
            {search.loading && search.items.length === 0 ? (
              <span className="spinner-border spinner-border-sm text-primary" />
            ) : (
              <>
                <strong>{search.total}</strong>{' '}
                {search.total === 1 ? 'solución' : 'soluciones'}
                {search.query.trim() && ` para «${search.query.trim()}»`}
              </>
            )}
          </div>

          <ul className="sl-list">
            {!search.loading && search.items.length === 0 && (
              <li className="sl-empty">
                {search.query.trim()
                  ? 'No hay soluciones que coincidan.'
                  : 'Aún no hay soluciones registradas.'}
              </li>
            )}
            {search.items.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`sl-item ${selected?.id === s.id ? 'is-active' : ''}`}
                  onClick={() => select(s.id)}
                  aria-current={selected?.id === s.id ? 'true' : undefined}
                >
                  <span className="sl-item-top">
                    <span className="sl-item-title">{s.title}</span>
                    <span className="sl-item-date">
                      {shortDate(s.createdAt)}
                    </span>
                  </span>
                  <span className="sl-item-preview">
                    {htmlToPlainText(s.description || '') || 'Sin descripción'}
                  </span>
                  <span className="sl-item-meta">
                    #{s.id}
                    {s.ticketIds.length > 0 && (
                      <span>
                        <i className="fa-solid fa-ticket" />
                        {s.ticketIds.length}{' '}
                        {s.ticketIds.length === 1 ? 'ticket' : 'tickets'}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
            {search.hasMore && (
              <li>
                <button
                  type="button"
                  className="sl-more"
                  onClick={search.loadMore}
                  disabled={search.loading}
                >
                  {search.loading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    `Cargar más (${search.total - search.items.length})`
                  )}
                </button>
              </li>
            )}
          </ul>
        </aside>

        {/* DETALLE */}
        <section className="sl-detail-pane">
          <SolutionDetail
            solution={selected}
            author={selected ? employeeByAuthId[selected.createdBy] : null}
            onBack={() => setShowDetailMobile(false)}
          />
        </section>
      </div>

      <style>{`
        .sl-layout { flex: 1 1 auto; min-height: 0; display: flex; gap: 12px; }
        .sl-list-pane, .sl-detail-pane {
          min-height: 0; border: 1px solid var(--gm-border); border-radius: 12px; background: var(--gm-surface);
        }
        .sl-list-pane { flex: 0 0 380px; display: flex; flex-direction: column; overflow: hidden; }
        .sl-detail-pane { flex: 1 1 auto; min-width: 0; overflow-y: auto; padding: 18px 20px; }

        .sl-search {
          display: flex; align-items: center; gap: 8px; margin: 10px 10px 0; height: 34px; padding: 0 12px;
          border: 1px solid var(--gm-border); border-radius: 8px; background: var(--gm-surface-2);
          color: var(--gm-text-faint); transition: border-color 0.15s ease;
        }
        .sl-search:focus-within { border-color: var(--gm-primary); }
        .sl-search i { font-size: 0.8rem; }
        .sl-search input { flex: 1; min-width: 0; border: none; outline: none; background: transparent; font-size: 0.85rem; color: var(--gm-text); }
        .sl-count { padding: 8px 14px; font-size: 0.75rem; color: var(--gm-text-muted); border-bottom: 1px solid var(--gm-border); }
        .sl-count strong { color: var(--gm-text); }

        .sl-list { list-style: none; margin: 0; padding: 4px; overflow-y: auto; flex: 1 1 auto; min-height: 0; }
        .sl-empty { padding: 24px 12px; text-align: center; font-size: 0.82rem; color: var(--gm-text-faint); }
        .sl-item {
          display: flex; flex-direction: column; gap: 3px; width: 100%; padding: 9px 10px; text-align: left;
          border: none; border-radius: 8px; background: transparent; transition: background 0.1s ease;
        }
        .sl-item:hover { background: var(--gm-surface-2); }
        .sl-item.is-active { background: var(--gm-primary-soft); }
        .sl-item-top { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
        .sl-item-title {
          flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-size: 0.86rem; font-weight: 600; color: var(--gm-text);
        }
        .sl-item.is-active .sl-item-title { color: var(--gm-primary); }
        .sl-item-date { flex-shrink: 0; font-size: 0.7rem; color: var(--gm-text-faint); }
        /* Resumen en una sola línea: el contenido completo va en el detalle. */
        .sl-item-preview {
          display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-size: 0.78rem; color: var(--gm-text-muted);
        }
        .sl-item-meta { display: flex; gap: 10px; font-size: 0.68rem; color: var(--gm-text-faint); font-variant-numeric: tabular-nums; }
        .sl-item-meta i { margin-right: 4px; }
        .sl-more {
          width: 100%; margin-top: 4px; padding: 8px; border: 1px dashed var(--gm-border); border-radius: 8px;
          background: transparent; color: var(--gm-primary); font-size: 0.8rem; font-weight: 600;
        }

        .sd-back { display: none !important; }
        @media (max-width: 767px) {
          .sl-list-pane { flex: 1 1 auto; }
          .sl-detail-pane { display: none; }
          .sl-layout.show-detail .sl-list-pane { display: none; }
          .sl-layout.show-detail .sl-detail-pane { display: block; }
          .sd-back { display: inline-flex !important; }
        }
      `}</style>
    </div>
  )
}

export default Solutions
